import { useEffect, useRef, useState } from "react";
import MasterIcon from "../../../../../assets/images/icons/master-icon.svg";
import DownloadIcon from "../../../../../assets/images/icons/download.svg";
import { ReactComponent as MixIconSVG } from "../../../../../assets/images/icons/mix-icon.svg";
import MoreIcon from "../../../../../assets/images/icons/option.svg";
import { ReactComponent as WaveFormSVG } from "../../../../../assets/images/icons/wave.svg";
import { Music, Trash2, Edit3, Play, Pause, Share2, Link2, Music2Icon, ListMusicIcon } from "lucide-react";
import { Tooltip } from "../../../../../components/Tooltip";
import DropdownMenu from "../../../../../components/DropdownMenu";
import { useDispatch, useSelector } from "react-redux";
import { extractFileName } from "../../../../../helper/extractFileName";
import { downloadMasterTrack, previewData, renameMasterTrack, uploadAndGetPreview, uploadData, uploadFileFromUrls } from "../../../../../redux/actions/audioEngineer";
import { useDeleteMixedTrack, useRenameMixedTrack } from "../../../../../queries/urstudio/mixingengineer.queries";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Dialog from "../../../../../components/Dialogbox";
import SendFilePopup from "../../AudioEngineer/sections/SendFilePopup";
import WaveformPlayer from "../../AudioEngineer/sections/Waveform";
import { timeAgo, toMinSec } from "../../../../../helper/dataFormat";
import { useNavigate, useLocation } from "react-router-dom";
import { getFileExtensionRegex } from "../../../../../helper/utility";
import { showToast } from "../../../../../helper/ShowToast";
import { useUpdateMixAnalysisReport } from "../../../../../queries/urstudio/mixanalyst.query";
import { useQueryClient } from '@tanstack/react-query';
import { consumeCredits } from "../../../../../redux/actions";
import { MixAnalystPackModal } from "../../MixAnalyst/MixAnalystPackModal";
import { CreditPlanModal } from "../../../../../components/CreditPlanModal";
import StemSplitsLibrary from "../../MixEnhancer/StemSplits/StemSplitsLibrary";

function convertToMB(fileSize) {
    const MB = fileSize / (1024 * 1024);
    return MB.toFixed(2); // Returns with 2 decimal places
}

export const MixesTrackTable = ({ libraryData, selectedTracks, setSelectedTracks }) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const location = useLocation()
    const queryClient = useQueryClient()
    const uploadedData = useSelector((state) => state.audioEngineer?.uploadedData);
    const [showChargeModal, setShowChargeModal] = useState(false);
    const [showCreditPlanModal, setShowCreditPlanModal] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false)
    const [hoveredTrack, setHoveredTrack] = useState(null);
    const [isMoving, setIsMoving] = useState(false)
    const [isMastering, setIsMastering] = useState(false)
    const [isPlaying, setIsPlaying] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [selectedTrack, setSelectedTrack] = useState(null)
    const [tempName, setTempName] = useState("");
    const [forceRender, setForceRender] = useState(false);
    const [progress, setProgress] = useState({});
    const [reqData, setReqData] = useState(null)
    const [stemTrackId, setStemTrackId] = useState(null)
    const [stemsData, setStemsData] = useState(null)
    const [showStems, setShowStems] = useState(false)
    const audioRef = useRef(null);
    const progressRef = useRef({})
    const localGenre = localStorage.getItem('genre')
    const { hasActiveSubscription, creditDetails } = useSelector((state) => state.billing);

    const handleCheckboxChange = (trackId) => {
        setSelectedTracks(prev => {
            if (prev.includes(trackId)) {
                return prev.filter(id => id !== trackId);
            } else {
                return [...prev, trackId];
            }
        });
    };

    const getMenuItems = (track) => [
        {
            label: "Rename...",
            icon: <Edit3 size={16} />,
            onClick: () => handleRename(track),  // pass id here
        },
        {
            label: "Share File",
            icon: <Share2 size={16} />,
            onClick: () => handleEmailDialog(track),
        },
        {
            label: "Get Link",
            icon: <Link2 size={16} />,
            onClick: () => {
                showToast("info", "Mix URL copied to clipboard")
                navigator.clipboard.writeText(track?.mixedUrl);
            },  // pass id here
        },
        {
            label: "Move to Multitrack",
            icon: <Music2Icon size={16} />,
            onClick: () => handleMoveToMultitrackSingle(track),
        },
        {
            label: "Delete",
            icon: <Trash2 size={16} />,
            danger: true,
            onClick: () => handleDialog(track),
        },
    ];

    const downloadAudioFile = (url, format, fileName) => {
        const reqBody = {
            url: url,
            format: format,
            fileName: fileName,
        }
        dispatch(downloadMasterTrack(reqBody, () => { }))
    }

    const getDownloadItems = (track) => [
        {
            label: "Download as WAV file",
            icon: <Music size={16} />,
            onClick: () => downloadAudioFile(track?.mixedUrl, "wav", track.projectName),
        },
        {
            label: "Download as MP3 file",
            icon: <Music size={16} />,
            onClick: () => downloadAudioFile(track?.mixedUrl, 'mp3', track.projectName),
        },
        {
            label: "Download as M4A file",
            icon: <Music size={16} />,
            onClick: () => downloadAudioFile(track?.mixedUrl, 'm4a', track.projectName),
        },
        {
            label: "Download as AAC file",
            icon: <Music size={16} />,
            onClick: () => downloadAudioFile(track?.mixedUrl, 'aac', track.projectName),
        },
        {
            label: "Download as OGG file",
            icon: <Music size={16} />,
            onClick: () => downloadAudioFile(track?.mixedUrl, 'ogg', track.projectName),
        },
        {
            label: "Download as AIFF file",
            icon: <Music size={16} />,
            onClick: () => downloadAudioFile(track?.mixedUrl, 'aiff', track.projectName),
        }
    ];

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 5; // show 5 rows per page (adjust as needed)

    const tracks = libraryData || [];
    const totalPages = Math.ceil(tracks.length / pageSize);
    const paginatedTracks = tracks.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const handleRename = (track) => {
        setEditingId(track._id);
        setTempName(extractFileName(track.projectName));
    };

    const deleteMutation = useDeleteMixedTrack();
    const renameMutation = useRenameMixedTrack();

    const handleDelete = () => {
        if (!selectedTrack) return;
        deleteMutation.mutate(selectedTrack._id, {
            onSuccess: () => {
                // close dialog and remove from selection if present
                setIsDialogOpen(false);
                setSelectedTracks(prev => prev?.filter(id => id !== selectedTrack._id));
            }
        });
    };

    const handleDialog = (track) => {
        setSelectedTrack(track);
        setIsDialogOpen(!isDialogOpen)
    }

    const handleEmailDialog = (track) => {
        setSelectedTrack(track);
        setIsEmailDialogOpen(!isEmailDialogOpen)
    }

    const handleMoveToMultitrackSingle = (track) => {
        if (!track) return;
        try {
            // map track to uploadedData shape expected by MultiTrack
            const mapped = {
                _id: track._id || track.id,
                filename: track.filename || track.projectName || track.name,
                readable_url: track.readable_url || track.mixedUrl || track.fileUrl || track.originalUrl,
                originalUrl: track.originalUrl || track.original_url || track.mixedUrl || track.fileUrl,
            };

            // merge with existing uploadedData
            let merged = [];
            if (uploadedData) {
                if (Array.isArray(uploadedData.data)) merged = [...uploadedData.data];
                else if (Array.isArray(uploadedData)) merged = [...uploadedData];
            }
            // avoid duplicates by _id
            const exists = merged.some(i => String(i._id) === String(mapped._id));
            if (!exists) merged.push(mapped);

            const requestBody = {
                sourceUrls: [mapped.originalUrl]
            }
            dispatch(uploadFileFromUrls(requestBody, setIsMoving, 'mixing-engineer', navigate))
            // dispatch(uploadData(payload));
            // navigate to mixing-engineer (Multitrack tab default)
            // navigate('/admin/urstudio/mixing-engineer#movetoMultitrack');
        } catch (e) {
            console.debug('Move to Multitrack failed', e);
        }
    }

    const handleMasterCreation = (track) => {
        if (!track) return;
        try {
            // map track to uploadedData shape expected by MultiTrack
            const mapped = {
                _id: track._id || track.id,
                filename: track.filename || track.projectName || track.name,
                readable_url: track.readable_url,
                originalUrl: track.mixedUrl,
            };

            // merge with existing uploadedData
            let merged = [];
            if (uploadedData) {
                if (Array.isArray(uploadedData.data)) merged = [...uploadedData.data];
                else if (Array.isArray(uploadedData)) merged = [...uploadedData];
            }
            // avoid duplicates by _id
            const exists = merged.some(i => String(i._id) === String(mapped._id));
            if (!exists) merged.push(mapped);

            const payload = Array.isArray(uploadedData) ? merged : Object.assign({}, uploadedData || {}, { data: merged });
            const requestBody = {
                sourceUrls: [mapped.originalUrl]
            }
            dispatch(uploadFileFromUrls(requestBody, setIsMastering, 'mastering', navigate))
        } catch (e) {
            console.debug('Move to Multitrack failed', e);
        }
    }

    const handlePlayPause = (track) => {
        if (isPlaying === track._id) {
            audioRef.current.pause();
            setIsPlaying(null);
        } else {
            if (audioRef.current) {
                audioRef.current.pause();
            }
            audioRef.current = new Audio(track.mixedUrl);
            audioRef.current.play();
            setIsPlaying(track._id);

            audioRef.current.ontimeupdate = () => {
                const percent =
                    (audioRef.current.currentTime / audioRef.current.duration) * 100;
                progressRef.current[track._id] = percent;
                setForceRender((p) => !p);
            };

            audioRef.current.onended = () => {
                setIsPlaying(null);
                progressRef.current[track._id] = 0;
                setForceRender((p) => !p);
            };
        }
    };

    const mutateMixAnalysisReport = useUpdateMixAnalysisReport()
    const { isPending } = mutateMixAnalysisReport

    const handleGenerateReport = (track) => {
        if (track?.isReportGenerated) {
            window.open(`/admin/urstudio/analysis-report?id=${track._id}`, '_blank')
        } else {
            const reqBody = {
                id: track._id,
                fileLocation: track?.mixedUrl,
                musicalStyle: localGenre === 'ROCK_INDIE' ? 'INDIE_ROCK' : localGenre === 'HIPHOP_GRIME' ? 'HIP_HOP_GRIME' : localGenre,
                isMaster: false,
                isMixed: true
            }
            setReqData(reqBody)
            if (hasActiveSubscription) {
                if (!creditDetails?.data || creditDetails?.data?.remaining <= 3) {
                    showToast("info", "You are out of AI credits. Please purchase more to continue.");
                    setShowCreditPlanModal(true);
                    return;
                }
                mutateMixAnalysisReport.mutate(reqBody, {
                    onSuccess: () => {
                        dispatch(consumeCredits(3))
                        setTimeout(() => {
                            window.open(`/admin/urstudio/analysis-report?id=${track._id}`, '_blank')
                        }, 2000)
                        queryClient.invalidateQueries(['mixes-library'])
                    }
                })
            } else {
                setShowChargeModal(true)
            }
        }
    }

    // Stop any playing track when the user navigates to another page
    useEffect(() => {
        return () => {
            if (audioRef.current && !audioRef.current.paused) {
                try {
                    audioRef.current.pause();
                } catch (e) {
                    console.warn("Failed to stop library audio on page change", e);
                }
            }
        };
    }, []);

    // Stop any playing track when the route changes while the component is still mounted
    useEffect(() => {
        if (audioRef.current && !audioRef.current.paused) {
            try {
                audioRef.current.pause();
            } catch (e) {
                console.warn("Failed to stop library audio on route change", e);
            }
        }
        setIsPlaying(null);
    }, [location.pathname]);

    const handleShowStems = (track) => {
        setStemTrackId(track._id);
        setStemsData(track?.stems)
        setShowStems(true);
    }
    return (
        <>
            <tbody>
                {isMoving &&
                    <tr>
                        <td colSpan="7" className="text-center text-gray-400 py-6">Moving your track to Multitrack...</td>
                    </tr>
                }
                {isMastering &&
                    <tr>
                        <td colSpan="7" className="text-center text-gray-400 py-6">Processing your track for Master Release...</td>
                    </tr>
                }
                {paginatedTracks?.length === 0 && (
                    <tr>
                        <td colSpan="7" className="text-center text-gray-400 py-6">No data available</td>
                    </tr>
                )}
                {isPending && (
                    <tr>
                        <td colSpan="7" className="text-center text-gray-400 py-6">Generating your Mix Report...</td>
                    </tr>
                )}
                {(paginatedTracks || []).map((track, idx) => (
                    <tr
                        key={track._id}
                        className="masters-library-table hover:bg-[#11175c] transition-colors rounded-lg"
                        onMouseEnter={() => setHoveredTrack(track._id)}
                        onMouseLeave={() => setHoveredTrack(null)}
                    >
                        <td className="p-2">
                            <input
                                type="checkbox"
                                checked={selectedTracks?.includes(track._id)}
                                onChange={() => handleCheckboxChange(track._id)}
                                className="rounded border-gray-400 bg-transparent text-[#FA6138] focus:ring-[#FA6138] cursor-pointer"
                                style={{ backgroundColor: 'transparent' }}
                            />
                        </td>
                        <td className="p-2">
                            <div className="max-w-[300px] flex items-center gap-3">
                                <div className="bg-[#FA6138] p-2 rounded flex items-center justify-center min-w-[35px] w-[35px] h-[35px]">
                                    {isPlaying === track._id ? (
                                        // Currently playing — always show Pause (no hover required)
                                        <button onClick={() => handlePlayPause(track)}>
                                            <Pause size={20} color="#ffffff" />
                                        </button>
                                    ) : hoveredTrack === track._id ? (
                                        // Not playing but hovered — show Play
                                        <button onClick={() => handlePlayPause(track)}>
                                            <Play size={20} color="#ffffff" />
                                        </button>
                                    ) : (
                                        // Not playing, not hovered — show static Music icon
                                        <Music size={20} color="#ffffff" />
                                    )}
                                </div>
                                <div>
                                    <p className="text-white text-sm font-medium break-all">
                                        {editingId === track._id ? (
                                            <input
                                                type="text"
                                                value={tempName}
                                                autoFocus
                                                onChange={(e) => setTempName(e.target.value)}
                                                onBlur={() => {
                                                    // save changes when input loses focus
                                                    renameMutation.mutate({ id: track._id, body: { newName: tempName } })
                                                    setEditingId(null);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        renameMutation.mutate({ id: track._id, body: { newName: tempName } })
                                                        setEditingId(null);
                                                    }
                                                    if (e.key === "Escape") {
                                                        setEditingId(null); // cancel rename
                                                    }
                                                }}
                                                className="bg-transparent border-b border-gray-400 text-white outline-none"
                                            />
                                        ) : renameMutation.isPending && renameMutation.variables?.id === track._id ? (
                                            <span className="inline-flex items-center gap-2 text-gray-300 text-sm">
                                                <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                Renaming...
                                            </span>
                                        ) : deleteMutation.isPending && deleteMutation.variables === track._id ? (
                                            <span className="inline-flex items-center gap-2 text-gray-300 text-sm">
                                                <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                Deleting...
                                            </span>
                                        ) : (
                                            extractFileName(track.projectName) || extractFileName(track.filename)
                                        )}
                                    </p>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-xs text-gray-300">
                                                {getFileExtensionRegex(track?.mixedUrl).toUpperCase()}
                                            </span>
                                            <span className="bg-white text-[#00067e] text-nowrap text-xs px-2 py-1 rounded-xl">
                                                {
                                                    track?.isMixEnhanced && track?.isReportGenerated ? "ENHANCED MIX & ANALYSIS READY"
                                                        : track?.isMixEnhanced && track?.isFinalMixed ? "ENHANCED MIX READY"
                                                            : track?.isReportGenerated ? "MIX & ANALYSIS READY"
                                                                : track?.isFinalMixed && !track?.isMixEnhanced && !track?.isReportGenerated ? "MIX READY"
                                                                    : ''
                                                }
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </td>
                        {/* <td className="p-2">
                            <img
                                src={WaveFormImage}
                                className="h-4 sm:h-6"
                                alt="Waveform"
                            />
                        </td> */}
                        {/* Waveform with Progress */}
                        <td className="p-2">
                            <div className="flex items-center gap-3 w-full">

                                {/* Icon 1 — Stems */}
                                {track.stemProcessing &&
                                    <Tooltip
                                        text={"Show Stem splits"}
                                        style={{ bg: "bg-white", text: "text-theme_clr1", width: "w-auto" }}
                                        position={idx === 0 ? "bottom" : "top"}
                                    >
                                        <div onClick={() => handleShowStems(track)}>
                                            <ListMusicIcon className="cursor-pointer shrink-0" size={30} />
                                        </div>
                                    </Tooltip>
                                }

                                {/* Icon 2 — Mix Report */}
                                <Tooltip
                                    text={track.isReportGenerated ? "Show Mix Report" : "Generate Analysis Report"}
                                    style={{ bg: "bg-white", text: "text-theme_clr1", width: "w-auto" }}
                                    position={idx === 0 ? "bottom" : "top"}
                                >
                                    <MixIconSVG
                                        onClick={() => handleGenerateReport(track)}
                                        className="cursor-pointer shrink-0"
                                    />
                                </Tooltip>

                                {/* Icon 3 — Waveform (takes remaining space) */}
                                <div className="relative grow min-w-24 h-12 overflow-hidden">
                                    {/* Base waveform (gray) */}
                                    <WaveFormSVG className="absolute inset-0 w-full h-full opacity-30 object-fill" />
                                    {/* Progress waveform (white overlay) */}
                                    <div
                                        className="absolute inset-0 h-full overflow-hidden transition-all duration-200"
                                        style={{ width: `${progress[idx + 1] || 0}%` }}
                                    >
                                        <WaveFormSVG className="w-full h-full object-fill" />
                                    </div>
                                </div>

                            </div>
                        </td>
                        <td className="p-2 text-white">{toMinSec(track?.duration) || "..."}</td>
                        <td className="p-2 text-gray-300">{timeAgo(track?.createdAt) || "..."}</td>
                        <td className="p-2 text-white">{convertToMB(track?.fileSize) + "MB" || "..."}</td>
                        <td className="p-2">
                            <div className="flex items-center gap-2">
                                {[MasterIcon, DownloadIcon, MoreIcon].map(
                                    (icon, i) => (
                                        <Tooltip
                                            text={
                                                i === 0
                                                    ? "Master"
                                                    : i === 1
                                                        ? "Download"
                                                        : "More Options"
                                            }
                                            style={{
                                                bg: "bg-white",
                                                text: "text-theme_clr1",
                                                width: "w-auto",
                                            }}
                                            position={idx === 0 ? "bottom" : i === 2 ? "top-right" : "top"}
                                        >
                                            {i === 0 ? (
                                                <button onClick={() => handleMasterCreation(track)} className="bg-white flex items-center justify-center rounded-full text-black hover:bg-theme_clr2 h-[30px] w-[100px]">
                                                    <img src={icon} alt="" className="block mr-1" /> Master
                                                </button>
                                            ) : i === 1 ? (
                                                <DropdownMenu
                                                    button={
                                                        <button className="bg-white flex items-center justify-center rounded-full hover:bg-theme_clr2 h-[30px] w-[30px]">
                                                            <img src={icon} alt="" className="block" />
                                                        </button>
                                                    }
                                                    items={getDownloadItems(track)}
                                                />
                                            ) : i === 2 ? (
                                                <DropdownMenu
                                                    button={
                                                        <button className="bg-white flex items-center justify-center rounded-full hover:bg-theme_clr2 h-[30px] w-[30px]">
                                                            <img src={icon} alt="" className="block" />
                                                        </button>
                                                    }
                                                    items={getMenuItems(track)}
                                                />
                                            ) : (
                                                <button
                                                    onClick={() => {
                                                        navigate('/admin/urstudio/mixing-engineer')
                                                        dispatch(previewData({ ...track, previewUrl: track?.mixedUrl, isMastered: true, playNow: true }))
                                                    }}
                                                    key={i}
                                                    className="bg-white flex items-center justify-center rounded-full hover:bg-theme_clr2 h-[30px] w-[30px]"
                                                >
                                                    <img src={icon} alt="" className="block" />
                                                </button>
                                            )}
                                        </Tooltip>
                                    )
                                )}
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
            {/* Pagination Controls */}
            {totalPages > 1 && !isMoving && !isMastering && (
                <tfoot>
                    <tr>
                        <td colSpan="7" className="p-4">
                            <div className="flex justify-center items-center gap-3 text-white">
                                <button
                                    className="px-3 py-1 bg-theme_clr1 rounded disabled:opacity-50"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage((p) => p - 1)}
                                >
                                    Previous
                                </button>

                                {[...Array(totalPages)].map((_, i) => (
                                    <button
                                        key={i}
                                        className={`px-3 py-1 rounded ${currentPage === i + 1 ? "bg-theme_clr2 text-white" : "bg-theme_clr1"
                                            }`}
                                        onClick={() => setCurrentPage(i + 1)}
                                    >
                                        {i + 1}
                                    </button>
                                ))}

                                <button
                                    className="px-3 py-1 bg-theme_clr1 rounded disabled:opacity-50"
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage((p) => p + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        </td>
                    </tr>
                </tfoot>
            )}
            <Dialog title={"Delete Confirmation"} isOpen={isDialogOpen} hasActiveSubscription={hasActiveSubscription} onClose={handleDialog} onSave={handleDelete} leftButton={'No'} rightButton={'Yes'}>
                <div className="mb-6">
                    <h3 className="font-medium text-theme_clr2 mb-3">
                        Are you sure, you want to delete this track?
                    </h3>
                    <div className="bg-theme_clr1 text-gray-300 p-4 rounded-xl flex flex-col gap-3">
                        <p>
                            It will be removed from database permanently and cannot be restored.
                        </p>
                    </div>
                </div>
            </Dialog>
            <Dialog
                title={""}
                isOpen={showStems}
                hasActiveSubscription={false}
                onClose={() => setShowStems(false)}
                onSave={() => { }}
                needSpace={false}
                whiteCrossButton
            >
                <StemSplitsLibrary
                    addNew={() => { }}
                    handleSettings={() => { }}
                    trackId={stemTrackId}
                    data={stemsData}
                />
            </Dialog>
            <SendFilePopup isOpen={isEmailDialogOpen} onClose={() => setIsEmailDialogOpen(false)} fileUrl={selectedTrack?.mixedUrl} />
            <MixAnalystPackModal
                isOpen={showChargeModal}
                onClose={() => setShowChargeModal(false)}
                requestData={reqData}
                setMixReport={() => { }}
                setIsReportGenerated={() => { }}
                fromLibrary={true}
            />
            <CreditPlanModal
                isOpen={showCreditPlanModal}
                onClose={() => setShowCreditPlanModal(false)}
            />
        </>
    );
};